import type { FormValues } from '@/components/Form/types';
import type { FormikErrors, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';

import classes from './styles/form.module.scss';

type ValidationSchema = {
  describe?: (options?: { value?: unknown }) => unknown;
};

interface FormikDebugOverlayProps {
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  validationSchema: ValidationSchema | null | undefined;
  values: FormValues;
}

const formatDebugValue = (value: unknown): string => {
  return JSON.stringify(
    value,
    (_key, nestedValue: unknown) => {
      if (typeof File !== 'undefined' && nestedValue instanceof File) {
        return {
          name: nestedValue.name,
          size: nestedValue.size,
          type: nestedValue.type,
        };
      }
      return nestedValue;
    },
    2
  );
};

const FormikDebugOverlay = ({ errors, touched, validationSchema, values }: FormikDebugOverlayProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleFormikDebug = () => setIsVisible((currentValue) => !currentValue);
    window.debugFormik = toggleFormikDebug;

    return () => {
      if (window.debugFormik === toggleFormikDebug) {
        delete window.debugFormik;
      }
    };
  }, []);

  if (!isVisible) return null;

  const validators = validationSchema?.describe?.({ value: values }) ?? null;

  return (
    <aside className={classes.formikDebugOverlay} aria-label="Formik debug overlay">
      <h2>Formik debug</h2>
      <details open>
        <summary>Values</summary>
        <pre>{formatDebugValue(values)}</pre>
      </details>
      <details open>
        <summary>Validators</summary>
        <pre>{formatDebugValue(validators)}</pre>
      </details>
      <details open>
        <summary>Errors</summary>
        <pre>{formatDebugValue(errors)}</pre>
      </details>
      <details>
        <summary>Touched</summary>
        <pre>{formatDebugValue(touched)}</pre>
      </details>
    </aside>
  );
};

export default FormikDebugOverlay;
